import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from 'reactstrap';
import "./BuiltInTests.scss";
import { 
  calculateAverageTrialTime, 
  calculateAverageExtraActivations,
  beepAudio, 
  calculateDifficulty
} from "./resources/ReactionTestCalcs";
import DisplayReactionResults from "./DisplayReactionResults";
import { TEST_DRIVE_ACTIVITIES, SWITCH_TRIAL_STATES, 
  DEFAULT_HITS_IN_TEST, DEFAULT_TARGET_TYPE, TARGET_TYPES, NUM_PRACTICE_TRIALS } from "../../Views/ConstForTestDriveSection";
import { wait } from "@testing-library/react";

const ReactionTest = (props) => {
  const {
    runningPractice,
    disableInput,
    finished,
    setFinished,
    saveActivityResultsToLocalState,
    targetType = DEFAULT_TARGET_TYPE
  } = props;
  let {
    numTrials = DEFAULT_HITS_IN_TEST,
    statsToDisplay = [],   //component stats for the test (to display when finished, if you navigate back to the page)
  } = props;

  if(runningPractice)
    numTrials = NUM_PRACTICE_TRIALS;
  
  const [completedTests, setCompletedTests] = useState([]);
  const [started, setStarted] = useState(false);
  const [timeTargetAppears, setTimeTargetAppears] = useState(0);
  const [hiddenTime, setHiddenTime] = useState(0);
  const [lastHide, setLastHide] = useState(0);
  const [awaitedRelease, setAwaitedRelease] = useState(false);
  const [displayElement, setDisplayElement] = useState(false);
  const [waitingToDisplay, setWaitingToDisplay] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activationType, setActivationType] = useState("none");
  const [extraActivations, setExtraActivations] = useState(0);
  const [pressedKeys, setPressedKeys] = useState(new Map());
  const switchDownRef = useRef(false);
  const [switchDown, setSwitchDown] = useState(false);
  const [showTimeout, setShowTimeout] = useState(null);

  //component stats for the test (to display when finished, if you navigate back to the page)
  if (completedTests.length > 0){
    statsToDisplay = completedTests;
  }

  const resetTrial = useCallback(() => {
    setActivationType("none");
    setExtraActivations(0);
    setAwaitedRelease(false);
  }, []);

  const testCompleted = useCallback((pressTime) =>{

    setDisplayElement(false);
    setShowSuccess(true);
    setTimeout(() => {
        setShowSuccess(false);
    }, 1500);
    setLastHide(Date.now());
    const reactionTime = (pressTime - timeTargetAppears);
    const holdTime = Date.now() - pressTime;
    setCompletedTests((current) => [
      ...current, {
        activationType: activationType,
        reactionTime: reactionTime,
        holdTime: holdTime,
        trialTime: reactionTime + holdTime,
        hiddenTime: hiddenTime,
        awaitedRelease: awaitedRelease,
        extraActivations: extraActivations
      }]
    );
    resetTrial();
  }, [activationType, awaitedRelease, extraActivations, hiddenTime, resetTrial, timeTargetAppears]);

  const finishTest = useCallback(() => {
    //just make sure not to call this with empty data
    //(can happen if Continue from test and then Back to see results)
    if(saveActivityResultsToLocalState && completedTests && completedTests.length > 0){
      saveActivityResultsToLocalState({
        type: TEST_DRIVE_ACTIVITIES.SWITCH_BASIC,
        completedTests: completedTests, 
        averageTrialTime: (calculateAverageTrialTime(completedTests)/1000).toFixed(2),
        averageExtraActivations: calculateAverageExtraActivations(completedTests).toFixed(2),
        switchDifficulty: calculateDifficulty(completedTests).toFixed(2)
      });
    }
  }, [completedTests, saveActivityResultsToLocalState])

  const pressOccurred = useCallback((key) => {
    //console.debug("ReactionTest.pressOccurred: displayElement=[" + displayElement + "] key=[" + key + "] pressedKeys=", pressedKeys);
    if(disableInput){
      return;
    }
    if(started){
      const curTime = Date.now();
      if(!pressedKeys.size || pressedKeys.size === 0){
        setActivationType(key);
      }
      if(!pressedKeys.has(key)){
        setPressedKeys(map => map.set(key, curTime));
        setSwitchDown(true)
      }
    }
  }, [pressedKeys, started]);

  const releaseOccurred = useCallback(async (key) => {
    //console.debug("ReactionTest.releaseOccurred: displayElement=[" + displayElement + "] key=[" + key + "] pressedKeys=", pressedKeys);
    if(disableInput){
      return; 
    }
    if(started){
      if(displayElement){
        if(key === activationType){
          testCompleted(pressedKeys.get(key));
          setSwitchDown(false)
        }
        setPressedKeys(map => {
          map.delete(key);
          return map;
        });
      }else{
        setPressedKeys(map => {
          map.delete(key);
          return map;
        });
        setExtraActivations((prev) => prev + 1);
        setSwitchDown(false)
      }
    }
  }, [activationType, displayElement, pressedKeys, started, testCompleted]);

  const handleKeyDown = useCallback((event) => {
    switch(event.key){
      case ' ':
        pressOccurred("Space");
        break;
      default:
        pressOccurred(event.key);
        break;
    }
  }, [pressOccurred]);

  const handleKeyUp = useCallback((event) => {
    switch(event.key){
      case ' ':
        releaseOccurred("Space");
        break;
      default:
        releaseOccurred(event.key);
        break;
    }
  }, [releaseOccurred]);

  const handleMouseDown = () => {
      pressOccurred("Click");
  };

  const handleMouseUp = () => {
    releaseOccurred("Click");
  };

  const startGame = () => {
    setLastHide(Date.now());
    setStarted(true);
  };

  //called on End Activity, to end the test early
  // sets finished to true, which triggers useEffect() to end the test and triggers a re-render with test results
  const handleDone = () => {
    setFinished(true);
  }

  const displayTargetAfterTime = () => {
    if(displayElement){
      return;
    }
    let timeout;
    // Random time between 1 and 3 seconds for target to appear
    const waitTime = 3000; //Math.floor(Math.random() * 2000) + 1000;
    const timeToAppear = Date.now() + waitTime;
    setTimeTargetAppears(timeToAppear);
    setHiddenTime(timeToAppear - lastHide);

    //when the timer goes off, check to make sure the switch isn't down.
    //if it's not down, then show the prompt (hitState 'press')
    //if it is down, then continue to wait to show the prompt (maybe this is a 5th hitState? 'let go')
    timeout = setTimeout(async () => {
      if(!disableInput){
        if(!switchDownRef.current){
          // await beepAudio.play();
          setDisplayElement(true);
          setAwaitedRelease(waitingToDisplay); 
          setWaitingToDisplay(false);
        }else{
          setWaitingToDisplay(true);
        }
      }
    }, waitTime); // Wait for randomized time before the target appears
    
    //here we set the showTimeout function:
    //if the previous state already had a value, clear the timeout function.
    //otherwise use the timeout function above, to schedule the prompt to appear 
    setShowTimeout((prev) => {
      if(prev){
        clearTimeout(prev);
        if(!waitingToDisplay)
          setWaitingToDisplay(switchDownRef.current);
      }
      return timeout;
    });
    //cleanup
    return () => {
      clearTimeout(timeout);
      setShowTimeout(null);
    }
  };

  //Called on initial render, as first of the useEffects
  //Sets the current value of the switch Down ref. This is necessary for having the correct value when timeout occurs
  //Manages clearing and setting timeouts and setting waitingToDisplay when switch is pressed/released during wait time
  //Also called any time the switch state changes (from up to down, or down to up)
  //Dependency warning is due to missing showTimeout, but if we include it, we get infinite loop when test starts,
  //I think b/c displayTargetAfterTime sets showTimeout.
  useEffect(() =>{
    switchDownRef.current = switchDown;
    if(started && !displayElement){
      if(switchDown && showTimeout){
        clearTimeout(showTimeout);
        if(!waitingToDisplay)
          setWaitingToDisplay(true);
      }else if(!switchDown){
        return displayTargetAfterTime();
      }
    }
  }, [switchDown, pressedKeys]);

  //Called on initial render, 2nd of the useEffects
  //Manages finishing tests or setting an initial hide time on release after successful trial
  //Also called when target first appears, and then again when target complete.
  //Also called when displayElement changes (when initial circle appears, and when the trial is complete)
  useEffect(() => {
    if(!disableInput){
      if((completedTests.length >= numTrials) || finished){
        finishTest();
      }else if(started && !displayElement && !switchDown){
        return displayTargetAfterTime();
      }
    }
  }, [displayElement, completedTests, started, finished]);

  //Called on initial render, 3rd of the useEffects
  //adds event listeners for keyboard (and removes on cleanup).
  //tried setting this up to only call once at beginning of test, but no dice: the keyboard won't work as a switch
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    //cleanup by removing handlers
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [displayElement, completedTests, pressedKeys, handleKeyDown, handleKeyUp, testCompleted]);

  const computeDivClass = () => {
    if(disableInput)
      return(SWITCH_TRIAL_STATES.SUCCESS);
    if (displayElement && !switchDown)
      return(SWITCH_TRIAL_STATES.PRESS);
    if (displayElement && switchDown)
      return(SWITCH_TRIAL_STATES.RELEASE);
    if (showSuccess)
      return(SWITCH_TRIAL_STATES.SUCCESS);
    if (waitingToDisplay && switchDown){
      return(SWITCH_TRIAL_STATES.PRE_TRIAL_RELEASE);
    }
    if (!displayElement && !switchDown){
      return(SWITCH_TRIAL_STATES.PRE_TRIAL_PAUSE);
    }
  }

  return (
    <div>
      {!started && !finished &&
        <div className="aa-target-container" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
          <Button className="aa-reaction-start-button" color="primary" onClick={startGame}>Start Activity</Button>
        </div>
      }
      {started && !finished &&
        <div>
          <div className="aa-target-container" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
            <DisplayTrialState trialState={computeDivClass()} targetType={targetType} />
          </div>
          <div className="aa-review-row"> 
            <span className="aa-more-info float-start">Prompt {completedTests.length + 1} of {numTrials}</span>
            <Button className="aa-end-activity-btn float-end" onClick={handleDone}>End Activity</Button>
          </div>
        </div>
      }
      {finished && (<DisplayReactionResults completedTests={statsToDisplay}/>)}
    </div>
  );
};

function DisplayTrialState(props){
  var {
    trialState,
    targetType
   } = props;

   let trialClasses = trialState;
   if (trialState === SWITCH_TRIAL_STATES.PRE_TRIAL_PAUSE){
    trialClasses += ' aa-show-ready';
   }
   switch(targetType){
    case TARGET_TYPES.SMILEY:
      if ((trialState === SWITCH_TRIAL_STATES.SUCCESS) || (trialState === SWITCH_TRIAL_STATES.RELEASE))
        trialClasses += ' aa-show-success-smiley';
      else if (trialState === SWITCH_TRIAL_STATES.PRESS) 
        trialClasses += ' aa-show-flat-face';
      break;
    default:
      if(trialState === SWITCH_TRIAL_STATES.SUCCESS)
        trialClasses += ' aa-show-success-checkmark';
      break;
   }

  return(
    <div>
      <h1 className="aa-switch-message">{getTrialStateMsg(trialState)}</h1>
      <div className={trialClasses}>
      </div>
    </div>
  )
}

function getTrialStateMsg(trialState){

  switch(trialState){
    case(SWITCH_TRIAL_STATES.PRESS):
      return('Go!');
    case(SWITCH_TRIAL_STATES.RELEASE):
      return('');
    case(SWITCH_TRIAL_STATES.SUCCESS):
      return('Success!');
    case(SWITCH_TRIAL_STATES.PRE_TRIAL_PAUSE):
      return('Get ready...');
    case(SWITCH_TRIAL_STATES.PRE_TRIAL_RELEASE):
      return('Please release the switch');
    default:
      return('');
  }
}

export default ReactionTest;